'use client'

import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { usePersona } from '@/lib/persona'

/** Sign out goes through the template's logout route (a navigation, so a link). */
export const LOGOUT_HREF = `${process.env.NEXT_PUBLIC_BASE_PATH || ''}/api/auth/logout`

/** Rail foot: persona monogram, persona short name, "Agency SSO", sign out (artifact `renderRail`). */
export function SidebarPersona({ isCollapsed }: { isCollapsed: boolean }) {
  const { current } = usePersona()

  const signOut = (
    <a
      href={LOGOUT_HREF}
      aria-label='Sign out'
      className={cn(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-primary-foreground/70',
        'transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
      )}
    >
      <Icons.logout aria-hidden='true' className='h-4 w-4' strokeWidth={1.5} />
    </a>
  )

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl p-2.5',
        'border border-primary-foreground/[0.08] bg-primary-foreground/[0.04]',
        isCollapsed && 'flex-col gap-2 p-2'
      )}
    >
      <span
        role='img'
        aria-label={`Viewing as ${current.short}`}
        title={isCollapsed ? `Viewing as ${current.short}` : undefined}
        className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-cornflower text-xs font-semibold text-brand-navy'
      >
        {current.initials}
      </span>

      {!isCollapsed && (
        <div className='min-w-0 flex-1' aria-hidden='true'>
          <p className='truncate text-sm font-medium text-primary-foreground'>{current.short}</p>
          <p className='truncate text-xs text-primary-foreground/60'>Agency SSO</p>
        </div>
      )}

      {isCollapsed ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{signOut}</TooltipTrigger>
          <TooltipContent side='right' className='ml-2'>
            Sign out
          </TooltipContent>
        </Tooltip>
      ) : (
        signOut
      )}
    </div>
  )
}
